import { useCallback, useState, useMemo } from 'react';
import { Platform } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { chatsApi } from '@/lib/api';
import { useAuthStore, useChatStore, useModelStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { exportConversation, saveExportedBlob } from '@/constants/utils/conversationExport';

// Stable selector functions defined outside the component
const createChatSelector = (id) => (state) => state.chats.find(c => c.id === id);
const createPinnedSelector = (id) => (state) => {
  const needle = id == null || id === '' ? '' : String(id);
  if (!needle) return false;
  return (state.user?.pinnedConversations || []).some((item) => {
    const pinnedId = item?.id ?? item?._id ?? item;
    return pinnedId != null && String(pinnedId) === needle;
  });
};

export function useChat(id) {
  const router = useRouter();
  const pathname = usePathname();
  
  // Memoized store selectors
  const { openModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);

  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();

  // Chat data and state
  const chat = useChatStore(useCallback(createChatSelector(id), [id]));
  const isPinned = useAuthStore(useCallback(createPinnedSelector(id), [id]));
  
  // Memoized action selectors
  const renameRegularChat = useChatStore(state => state.renameChat);
  const archiveChat = useChatStore(state => state.archiveChat);
  const unarchiveChat = useChatStore(state => state.unarchiveChat);
  const deleteChat = useChatStore(state => state.deleteChat);
  const markTemporaryChatDeleted = useChatStore(state => state.markTemporaryChatDeleted);
  const setDeletingChatId = useChatStore(state => state.setDeletingChatId);
  const clearDeletingChatId = useChatStore(state => state.clearDeletingChatId);
  const togglePinChat = useAuthStore(state => state.togglePinChat);

  // Loading states
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Memoized derived values
  const isArchived = useMemo(() => chat?.isArchived, [chat?.isArchived]);
  const chatTitle = useMemo(() => chat?.title, [chat?.title]);
  const isTemporary = useMemo(() => chat?.flags?.isTemporary, [chat?.flags?.isTemporary]);
  const usePersonalization = chat?.flags?.usePersonalization === true;

  const saveTemporaryChat = useChatStore(state => state.saveTemporaryChat);

  // Chat operations
  const handleArchiveToggle = useCallback(async () => {
    // Same rule as delete: leave the thread only if the user is looking at it.
    // Archiving from Your Chats history used to always router.push('/app'),
    // which yanked people off the page and forced a fresh route chunk load.
    const isViewingChat = pathname === `/c/${id}`;
    try {
      setIsArchiving(true);
      if (isArchived) {
        await chatsApi.unarchiveChat(id);
        await unarchiveChat(id);
        addToast(t('chat.archive.unarchiveSuccess'), 'success');
      } else {
        await chatsApi.archiveChat(id);
        await archiveChat(id);
        addToast(t('chat.archive.success'), 'success');
        if (isViewingChat) {
          router.replace('/app');
        }
      }
    } catch (error) {
      console.error('Error toggling archive:', error);
      addToast(t('chat.archive.error'), 'error');
    } finally {
      setIsArchiving(false);
    }
  }, [isArchived, id, pathname, unarchiveChat, archiveChat, addToast, t, router]);

  const handleDelete = useCallback(async () => {
    // Only redirect to a fresh chat when the user is actually viewing the
    // chat being deleted (route is /c/{id}). Deleting from a list — e.g. the
    // collection page, sidebar, or history — should leave the user where they
    // are instead of yanking them to a new chat page.
    const isViewingDeletedChat = pathname === `/c/${id}`;
    try {
      setIsDeleting(true);
      setDeletingChatId(id);

      // Delete from API and store first, THEN navigate.
      // On iOS, navigating before the delete completes can cause a race
      // condition where the chat page re-fetches the deleted chat, gets a 404,
      // and shows the not-found page before the navigation to /app completes.
      // Temporary chats must use the cascade-delete endpoint so uploaded
      // files do not linger after the user thinks the chat is gone.
      if (isTemporary) {
        markTemporaryChatDeleted(id);
        await chatsApi.deleteTemporaryChat(id);
      } else {
        await chatsApi.deleteChat(id);
      }
      await deleteChat(id);
      addToast(t('chat.delete.success'), 'success');

      // Navigate after successful delete — use replace so user can't go back
      if (isViewingDeletedChat) {
        router.replace('/app');
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      addToast(t('chat.delete.error'), 'error');
      // Drop the local sidebar pointer even on error. The backend soft-delete is
      // idempotent and best-effort, so the chat is either already gone or can be
      // re-deleted; keeping the pointer here is what previously left orphaned,
      // un-openable empty chats littering the history.
      try {
        await deleteChat(id);
      } catch (storeErr) {
        console.error('Error removing chat from store after delete failure:', storeErr);
      }
      // Still navigate away on error — the chat may be partially deleted
      if (isViewingDeletedChat) {
        router.replace('/app');
      }
    } finally {
      // Delay clearing the deleting flag so the chat page's fetch guard
      // remains active during the navigation transition on iOS
      setTimeout(() => {
        clearDeletingChatId();
        setIsDeleting(false);
      }, 1000);
    }
  }, [id, pathname, isTemporary, deleteChat, markTemporaryChatDeleted, router, addToast, t, setDeletingChatId, clearDeletingChatId]);

  // Modal handlers
  const handleArchiveConfirm = useCallback(() => {
    showConfirmationModal({
      title: isArchived ? t('chat.archive.unarchiveTitle') : t('chat.archive.title'),
      message: isArchived ? t('chat.archive.unarchiveMessage') : t('chat.archive.message'),
      confirmText: isArchived ? t('chat.archive.unarchiveButton') : t('chat.archive.confirmButton'),
      isDangerous: !isArchived,
      options: {
        height: 180,
        width: 350,
      },
      cancelText: t('common.cancel'),
      onConfirm: async () => {
        await handleArchiveToggle();
      }
    });
  }, [isArchived, t, handleArchiveToggle, showConfirmationModal]);

  const handleDeleteConfirm = useCallback(() => {
    showConfirmationModal({
      title: t('chat.delete.title'),
      message: t('chat.delete.message'),
      confirmText: t('chat.delete.confirmButton'),
      cancelText: t('common.cancel'),
      isDangerous: true,
      options: {
        height: 180,
        width: 350,
      },
      onConfirm: async () => {
        await handleDelete();
      }
    });
  }, [t, handleDelete, showConfirmationModal]);

  const handleShare = useCallback(() => {
    openModal('ShareModal', {
      onClose: () => {},
      conversationId: id,
      chatTitle,
    });
  }, [openModal, id, chatTitle]);

  const handleTogglePinChat = useCallback(async () => {
    try {
      await togglePinChat(id);
      addToast(isPinned ? 'Chat unpinned successfully' : 'Chat pinned successfully', 'success');
    } catch (error) {
      console.error('Error toggling pin:', error);
      addToast('Failed to toggle pin', 'error');
    }
  }, [togglePinChat, id, isPinned, addToast]);

  const handleRename = useCallback(() => {
    showInputModal({
      title: "Rename Chat",
      subtitle: "Enter a new name for this chat",
      placeholder: "Chat name",
      primaryButtonTitle: "Save",
      initialValue: chatTitle,
      onSave: async (newName) => {
        if (newName?.trim()) {
          await renameRegularChat(id, newName.trim());
        }
      }
    });
  }, [chatTitle, id, renameRegularChat, showInputModal]);

  const handleRenameForce = useCallback((newName) => {
    renameRegularChat(id, newName);
  }, [id, renameRegularChat]);

  const handleSaveTemporary = useCallback(async () => {
    try {
      await saveTemporaryChat(id);
      addToast(t('chat.saveTemporary.success'), 'success');
    } catch (error) {
      console.error('Error saving temporary chat:', error);
      addToast(t('chat.saveTemporary.error'), 'error');
    }
  }, [id, saveTemporaryChat, addToast, t]);

  const handleSaveTemporaryConfirm = useCallback(() => {
    showConfirmationModal({
      title: t('chat.saveTemporary.title'),
      message: t('chat.saveTemporary.message'),
      confirmText: t('chat.saveTemporary.confirmButton'),
      cancelText: t('common.cancel'),
      isDangerous: false,
      options: {
        height: 220,
        width: 380,
      },
      onConfirm: async () => {
        await handleSaveTemporary();
      },
    });
  }, [t, handleSaveTemporary, showConfirmationModal]);

  const handleOpenTemporarySettings = useCallback(() => {
    openModal('TemporaryChatSettingsModal', {
      conversationId: id,
      initialUsePersonalization: usePersonalization,
    });
  }, [openModal, id, usePersonalization]);

  const handleExportChat = useCallback(async (format = 'markdown') => {
    try {
      if (!chat) {
        addToast(t('dropdowns.exportFailed'), 'error');
        return;
      }
      const getModelById = useModelStore.getState().getModelById;
      const hasLocalMessages = Array.isArray(chat.messages) && chat.messages.length > 0;
      // When the in-memory chat has no visible messages (e.g. older
      // messages have been archived out of the active array, or the
      // messages array simply hasn't been hydrated yet for a deep-linked
      // chat), building markdown/text client-side would yield an empty
      // file. Fall back to the server-side export, which merges archived
      // messages and can produce any format for any conversation we own.
      if (!hasLocalMessages && chat.id) {
        const result = await chatsApi.downloadConversationExport(chat.id, format);
        await saveExportedBlob(result, chat, format);
        addToast(t('dropdowns.exportSuccess'), 'success');
        return;
      }
      if (!hasLocalMessages) {
        addToast(t('dropdowns.exportFailed'), 'error');
        return;
      }
      await exportConversation(chat, { format, getModelById });
      addToast(t('dropdowns.exportSuccess'), 'success');
    } catch (error) {
      // Share-sheet dismissals are AbortError. Fetch timeouts are converted
      // to ApiError by fetchWithAuth, so they still surface a toast.
      if (error?.name === 'AbortError') return;
      console.error('Error exporting chat:', error);
      addToast(error?.name === 'ApiError' && error.message
        ? error.message
        : t('dropdowns.exportFailed'), 'error');
    }
  }, [chat, addToast, t]);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    // Data
    chat,
    isPinned,
    isArchived,
    isArchiving,
    isDeleting,
    isTemporary,
    usePersonalization,

    // Actions
    handleArchiveToggle,
    handleDelete,
    handleArchiveConfirm,
    handleDeleteConfirm,
    handleShare,
    handleTogglePinChat,
    handleRename,
    handleRenameForce,
    handleExportChat,
    handleSaveTemporaryConfirm,
    handleOpenTemporarySettings,
  }), [
    chat,
    isPinned,
    isArchived,
    isArchiving,
    isDeleting,
    isTemporary,
    usePersonalization,
    handleArchiveToggle,
    handleDelete,
    handleArchiveConfirm,
    handleDeleteConfirm,
    handleShare,
    handleTogglePinChat,
    handleRename,
    handleRenameForce,
    handleExportChat,
    handleSaveTemporaryConfirm,
    handleOpenTemporarySettings,
  ]);
}
